<template>
  <div class="mb-4 flex flex-col gap-y-4">
    <div class="rounded-[12px] border border-[#ebedef] bg-white p-[15px]">
      <div class="mb-5 flex items-center justify-between">
        <div class="text-[16px] font-medium leading-[20px] text-gray-800">
          Order Details
        </div>
        <button
          v-if="!hideChange"
          type="button"
          class="text-link text-[14px]"
          @click="emit('change')"
        >
          Change
        </button>
      </div>

      <div class="flex items-start justify-between gap-x-4">
        <div>
          <div class="text-[14px] font-medium leading-[20px] text-gray-800">
            {{ membershipName }}
          </div>
          <div
            v-if="subscriptionPriceItem"
            class="mt-1 text-[13px] font-normal leading-[18px] text-gray-600"
          >
            <template v-if="isBasicMembership">
              Your Basic Membership is free.
            </template>
            <template v-else>
              Your {{ membershipName }} renews {{ renewalCadence }} at
              {{ getBillingCyclePrice(subscriptionPriceItem) }} until you
              cancel.
            </template>
          </div>
        </div>
        <div
          v-if="subscriptionPriceItem"
          class="whitespace-nowrap text-[14px] font-medium leading-[20px] text-gray-800"
        >
          {{ getBillingCyclePrice(subscriptionPriceItem) }}
        </div>
      </div>

      <template v-if="registrationsLine">
        <div class="my-4 h-px bg-[#efefef]" />

        <div class="flex items-start justify-between gap-x-4">
          <div>
            <div class="text-[14px] font-medium leading-[20px] text-gray-800">
              {{ registrationsLabel }}
            </div>
            <div
              class="mt-1 text-[13px] font-normal leading-[18px] text-gray-600"
            >
              {{ membershipName }} pricing applies to every work on your
              account, including those previously registered.
            </div>
          </div>
          <div
            class="whitespace-nowrap text-[14px] font-medium leading-[20px] text-gray-800"
          >
            {{ registrationsLine.amount.formatted }}
          </div>
        </div>
      </template>

      <template v-if="outstandingLine">
        <div class="my-4 h-px bg-[#efefef]" />

        <div class="flex items-start justify-between gap-x-4">
          <div class="text-[14px] font-medium leading-[20px] text-gray-800">
            {{ outstandingLine.name }}
          </div>
          <div
            class="whitespace-nowrap text-[14px] font-medium leading-[20px] text-gray-800"
          >
            {{ outstandingLine.amount.formatted }}
          </div>
        </div>
      </template>

      <template v-if="showTotalDueToday">
        <div class="my-4 h-px bg-[#efefef]" />

        <div class="flex items-center justify-between">
          <div class="text-[16px] font-medium leading-[20px] text-gray-800">
            Total due today
          </div>
          <Skeleton v-if="awaitingEstimate" class="h-5 w-[70px]" />
          <div
            v-else
            class="text-[16px] font-medium leading-[20px] text-gray-800"
          >
            {{ totalDueToday }}
          </div>
        </div>
      </template>
    </div>

    <div
      v-if="subscriptionRenewal"
      class="rounded-[12px] border border-[#ebedef] bg-[#f9f9fa] px-[15px] py-3"
    >
      <div
        class="mb-2 flex items-center gap-x-2 text-[13px] font-medium leading-[18px] text-gray-800"
      >
        <ClockIcon class="size-[13px]" aria-hidden="true" />
        Future Billing
      </div>
      <div class="text-[13px] font-normal leading-[18px] text-gray-600">
        After today, {{ membershipName }} auto-renews at
        <span class="font-medium text-gray-600">{{ subscriptionRenewal }}</span
        ><template v-if="perWorkPrice"
          >, and each work you register is
          <span class="font-medium text-gray-600">{{ perWorkPrice }}</span
          >, billed within hours of registration</template
        >.
      </div>
    </div>

    <div
      v-if="selectedPlanItem?.upsell"
      class="flex flex-col gap-y-1.5 rounded-[12px] border border-green-700/25 bg-green-200/25 px-[15px] py-2.5 text-[14px] font-normal leading-[20px] text-gray-500"
    >
      <div class="flex items-center justify-between">
        <Pill class="mr-[15px]" variant="success">
          {{ selectedPlanItem.upsell.pillText }}
        </Pill>
        <button type="button" class="text-green-700" @click="emit('upgrade')">
          Apply upgrade
        </button>
      </div>
      {{ formatUpsellText(selectedPlanItem.upsell?.text) }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { ClockIcon } from '@dacr/icons'
import { Pill, Skeleton } from '@dacr/library'

import {
  MembershipTier,
  PlanPriceType,
  type Plan,
  type PlanItem,
  type PlanPriceItem,
} from '@/shared/graphql/legacy/generated'
import type { PlanChangeEstimate } from '@/features/billing/composables/useCheckout'

const props = defineProps<{
  selectedPlan?: Plan
  selectedPlanItem?: PlanItem
  selectedBillingCycle: { type: string; description: string }
  hasMoreThanOnePrice: boolean
  getBillingCyclePrice: (price: PlanPriceItem) => string
  formatUpsellText: (text?: string) => string | undefined
  registrationsEstimate?: PlanChangeEstimate | null
  awaitingEstimate?: boolean
  hideChange?: boolean
}>()

const emit = defineEmits<{
  (e: 'change'): void
  (e: 'upgrade'): void
}>()

const membershipName = computed(() => {
  const name = props.selectedPlan?.name ?? ''
  return props.selectedPlan?.membershipTier
    ? name.replace(/^Dacr\s+/, '')
    : name
})

const isBasicMembership = computed(
  () => props.selectedPlan?.membershipTier === MembershipTier.Basic
)

const subscriptionPriceItem = computed(() =>
  props.selectedPlanItem?.prices.find(
    (price) => price.type === PlanPriceType.Subscription
  )
)

const perWorkPriceItem = computed(() => {
  const price = props.selectedPlanItem?.prices.find(
    (item) => item.type === PlanPriceType.File
  )
  return price?.details?.amount ? price : undefined
})

const subscriptionRenewal = computed(() => {
  const price = subscriptionPriceItem.value
  return price
    ? `${props.getBillingCyclePrice(price)}/${props.selectedBillingCycle.type}`
    : undefined
})

const perWorkPrice = computed(() =>
  perWorkPriceItem.value
    ? props.getBillingCyclePrice(perWorkPriceItem.value)
    : undefined
)

const renewalCadence = computed(() =>
  props.selectedBillingCycle.type === 'month' ? 'monthly' : 'annually'
)

const registrationsLine = computed(
  () => props.registrationsEstimate?.fileLifetimeLine ?? null
)

const outstandingLine = computed(
  () => props.registrationsEstimate?.outstandingLine ?? null
)

const registrationsLabel = computed(() => {
  const quantity = registrationsLine.value?.quantity ?? 0
  return `${quantity} current Dacr registration${quantity === 1 ? '' : 's'}`
})

const totalDueToday = computed(
  () =>
    props.registrationsEstimate?.total.formatted ??
    (subscriptionPriceItem.value
      ? props.getBillingCyclePrice(subscriptionPriceItem.value)
      : undefined)
)

const showTotalDueToday = computed(
  () => props.awaitingEstimate || !!totalDueToday.value
)
</script>
